import Badge from 'components/badge/Badge';
import PropertyPicture from 'components/domain/property/picture/PropertyPicture';
import Switch from 'components/switch/Switch';
import {
  GetIntegrationAgencySettingsResponseTO,
  IntegrationEnum,
  MuSupportType,
} from 'models/InternalIntegrationSetting';
import { PropertyForPropertyListItem } from 'pages/agencyProperties/Properties.types';
import PropertyCollapsibleArea from 'pages/agencyProperties/item/PropertyCollapsibleArea';
import { useChildProperties } from 'pages/agencyProperties/item/PropertyItem.utils';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import theme from 'styles/theme';
import { isBusinessTypeSupported } from 'models/InternalIntegrationSetting.utils';
import ViewListingLink from 'pages/property/channels/ChannelListItem/ViewListingLink';
import {
  PropertyDetailsColumnStyled,
  PropertyListItemStyled,
  PropertyToggleContainerStyled,
} from 'pages/integrations/integrationDetails/components/propertiesList/propertiesResults/propertyListItem/PropertyListItem.styles';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import PropertyInfo from '../PropertyInfo';
import useToggleProperty from './useToggleProperty';
import {
  PropertyActionsColumnStyled,
  ToggleWithBadgeContainer,
} from './PropertyListItem.styles';

interface PropertyListItemProps {
  property: PropertyForPropertyListItem;
  isChildProperty?: boolean;
  integrationAgencySettings: GetIntegrationAgencySettingsResponseTO;
  pagePropertyUids: string[];
}

const PropertyListItem = ({
  property,
  isChildProperty,
  integrationAgencySettings,
  pagePropertyUids,
}: PropertyListItemProps) => {
  const { t } = useTranslation();
  const childProperties = useChildProperties(property);
  const {
    isIntegrationEnabled,
    isPendingStatus,
    toggleIntegration,
    isLoading,
    isUpdating,
    propertySetting,
  } = useToggleProperty({
    enumId: integrationAgencySettings.enumId,
    propertyUid: property.uid,
    pagePropertyUids,
  });

  const isPropertyWithChildren = childProperties.length > 0;

  const isPropertySupported = isBusinessTypeSupported({
    muSupportType:
      integrationAgencySettings.integration?.muSupportType ??
      MuSupportType.NONE,
    businessType: property.businessType,
  });

  const shouldShowInactiveBadge =
    !property.isActive &&
    integrationAgencySettings.enumId !== IntegrationEnum.FURNISHEDFINDER;

  const getActions = () => {
    if (!isPropertySupported) {
      return null;
    }

    if (isLoading) {
      return (
        <PropertyToggleContainerStyled>
          <LoadingPlaceholder width="128px" linesCount={1.5} />
        </PropertyToggleContainerStyled>
      );
    }

    return (
      <ToggleWithBadgeContainer>
        <Switch
          checked={isIntegrationEnabled}
          onChange={toggleIntegration}
          disabled={isUpdating}
          data-testid="property-toggle"
        />
        {isPendingStatus && (
          <Badge
            backgroundColor={theme.colors.hostfullyYellow}
            data-testid="pending-status"
          >
            {t('pageChannels.channelDetails.pending')}
          </Badge>
        )}
      </ToggleWithBadgeContainer>
    );
  };

  return (
    <PropertyListItemStyled data-property-uid={property.uid}>
      <Row>
        <PropertyDetailsColumnStyled $isChildProperty={isChildProperty}>
          <PropertyPicture
            property={property}
            target="_blank"
            rel="noreferrer"
          />
          <PropertyInfo property={property} />
        </PropertyDetailsColumnStyled>

        <PropertyActionsColumnStyled>
          <div>
            <ViewListingLink publicLink={propertySetting?.publicLink ?? null} />
            {getActions()}
          </div>

          {shouldShowInactiveBadge && (
            <Badge
              backgroundColor={theme.colors.gray600}
              data-testid="property-inactive-badge"
            >
              {t('pageChannels.channelDetails.propertyInactive')}
            </Badge>
          )}
        </PropertyActionsColumnStyled>
      </Row>

      {isPropertyWithChildren && (
        <PropertyCollapsibleArea property={property}>
          {childProperties.map((childProperty) => (
            <PropertyListItem
              key={childProperty.uid}
              property={childProperty}
              isChildProperty
              integrationAgencySettings={integrationAgencySettings}
              pagePropertyUids={pagePropertyUids}
            />
          ))}
        </PropertyCollapsibleArea>
      )}
    </PropertyListItemStyled>
  );
};

PropertyListItem.defaultProps = {
  isChildProperty: false,
};

export default PropertyListItem;
